import React from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "@fontsource-variable/noto-sans-thai";
import "@/styles/app.css";

import { routeTree } from "./routeTree.gen";
import { Toaster } from "@/components/ui/Toaster";
import i18n, { i18nReady } from "@/i18n";
import { createMutationCache } from "@/lib/mutationToasts";
import { LocalizedContentBridge } from "@/components/i18n/LocalizedContentBridge";

const mutationCache = createMutationCache();

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, err) => {
        const status = (err as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}

void i18nReady.then(() => {
  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <LocalizedContentBridge />
          <Toaster />
          {/* bottom-left so the dev button doesn't sit under the toast stack */}
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </QueryClientProvider>
      </I18nextProvider>
    </React.StrictMode>,
  );
});
